import { useEffect, useMemo } from "react";
import { useEvento } from "@/store/evento/evento";
import { useEventoContext } from "../../contexts/eventos";
import { SeleccionarFunciones } from "../funciones/SeleccionarFunciones";
import { CountdownBanner } from "../CountdownBanner";
import { FaqsAccordion } from "../FaqsAccordion";
import { ComoLlegar } from "../ComoLlegar";
import { InstagramYouTubeSection } from "./SocialEmbeds";
import { formatSeatLabel } from "@/lib/utils/seatLabel";

interface Props {
    onComprar: () => void;
    isComprarDisabled: boolean;
}

export const EventoContent = ({ onComprar, isComprarDisabled }: Props) => {
    const { state: activeTab, set: setActiveTab } = useEvento.use.tabs();
    const { state: entradas, removeAsiento } = useEvento.use.entradas();
    const { evento } = useEventoContext();

    const total = useMemo(() => {
        return entradas.reduce((acc, entrada) => {
            const qty = entrada.asientos?.length || 1;
            return acc + (entrada.precio * qty);
        }, 0);
    }, [entradas]);

    const maxEntradas = entradas.reduce((acc, entrada) => acc + (entrada.asientos?.length || 0), 0) >= 4;

    // Browser back button: go to info instead of leaving the page
    useEffect(() => {
        if (activeTab !== 'funciones') return;

        window.history.pushState({ tab: 'funciones' }, '');

        const handlePopState = () => {
            setActiveTab('detalles');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [activeTab, setActiveTab]);

    if (activeTab === 'funciones') {
        return (
            <div className="mt-8 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-4 md:px-8">
                {/* Back button */}
                <div className="mb-4">
                    <button
                        onClick={() => {
                            setActiveTab('detalles');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-gray-300 hover:text-white text-base font-medium flex items-center transition-colors"
                    >
                        <svg className="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        Volver a info
                    </button>
                </div>



                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left: Seat / ticket selection */}
                    <div className="lg:col-span-9 order-last lg:order-first">
                        <div className="w-full">
                            <SeleccionarFunciones />
                        </div>
                    </div>

                    {/* Right: Sticky summary card (desktop only) */}
                    <div className="hidden lg:block lg:col-span-3 order-first lg:order-last">
                        <div className="sticky top-4 space-y-3">
                            {maxEntradas && (
                                <div className="p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg text-center">
                                    <p className="text-amber-200 text-sm font-medium">
                                        ⚠️ Límite de 4 asientos por compra
                                    </p>
                                </div>
                            )}
                            <div className="bg-brand-red/10 border border-[#ff000014] rounded-lg overflow-hidden shadow-lg">
                                <div className="bg-brand-red text-white p-4">
                                    <h3 className="font-display text-lg">Resumen de compra</h3>
                                </div>
                                <div className="p-5">
                                    {entradas.length === 0 ? (
                                        <p className="text-gray-400 text-sm text-center py-4">
                                            Seleccioná asientos en el mapa
                                        </p>
                                    ) : (
                                        <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                                            {entradas.map((entrada, i) => (
                                                <div key={i} className="flex justify-between items-start text-sm">
                                                    <div className="flex-1">
                                                        <div className="text-white font-medium">
                                                            {entrada.nombre || 'Entrada'}
                                                        </div>
                                                        {entrada.descripcion && (
                                                            <div className="text-gray-500 text-xs mb-1">
                                                                {entrada.descripcion}
                                                            </div>
                                                        )}
                                                        {entrada.asientos?.map((a, j) => (
                                                            <div key={j} className="text-gray-400 text-xs flex items-center justify-between">
                                                                <span>{formatSeatLabel(a.seatId)}</span>
                                                                <button
                                                                    onClick={() => removeAsiento({ seatId: a.seatId, mesaId: a.mesaId, tipoEntradaId: entrada.tipoEntradaId, selected: false })}
                                                                    className="text-gray-500 hover:text-brand-red transition-colors ml-2 p-0.5"
                                                                    title="Quitar asiento"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <span className="text-white font-semibold whitespace-nowrap ml-3">
                                                        $ {(entrada.precio * (entrada.asientos?.length || 1)).toLocaleString("es-AR")}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {total > 0 && (
                                        <div className="flex justify-between items-center pt-3 mb-4">
                                            <span className="text-gray-300 font-medium">Total</span>
                                            <span className="text-white font-bold text-xl">$ {total.toLocaleString("es-AR")}</span>
                                        </div>
                                    )}

                                    <div className={`relative rounded-lg p-[2px] ${isComprarDisabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                                        <div className="absolute inset-0 rounded-lg animate-border-glow"
                                            style={{
                                                background: 'conic-gradient(from 0deg, transparent, #e63946, #b72733, transparent)',
                                            }}
                                        />
                                        <button
                                            onClick={onComprar}
                                            disabled={isComprarDisabled}
                                            className="relative w-full bg-brand-red uppercase hover:bg-[#c0392b] text-white font-bold py-3 rounded-[6px] transition-colors"
                                        >
                                            Comprar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile fixed bottom bar */}
                <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
                    <div className="bg-[#2a1a1c] border-t border-[#3d2224] pt-4 pb-4 px-4">
                        {maxEntradas && (
                            <div className="p-2 bg-amber-500/20 border border-amber-500/50 rounded-lg text-center mb-3">
                                <p className="text-amber-200 text-xs font-medium">
                                    ⚠️ Límite de 4 asientos por compra
                                </p>
                            </div>
                        )}
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-white">
                                {total > 0 ? (
                                    <>
                                        <div className="text-xs text-gray-400">Total</div>
                                        <div className="text-xl font-bold">$ {total.toLocaleString("es-AR")}</div>
                                    </>
                                ) : (
                                    <div className="text-sm text-gray-400">Seleccioná asientos</div>
                                )}
                            </div>
                            <button
                                onClick={onComprar}
                                disabled={isComprarDisabled}
                                className={`bg-brand-red uppercase hover:bg-[#c0392b] text-white font-bold py-3 px-8 rounded-lg transition-colors ${isComprarDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                Comprar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4">

            <div className="mt-10">
                <h2
                    className="font-display uppercase leading-none mb-6 text-[2rem] sm:text-[2vw] text-brand-red"
                >
                    Acerca del evento
                </h2>

                <div className="mb-8">
                    <p className="text-white font-bold text-lg tracking-wide uppercase">{evento.nombre}</p>

                </div>

                <div className="text-gray-300 space-y-6 whitespace-pre-wrap leading-relaxed text-[15px]">
                    {evento.descripcion}
                </div>

                <div className="mt-8 bg-brand-red/10 p-5 rounded-lg border-l-4 border-brand-red">
                    <ul className="text-gray-200 font-medium space-y-2 list-disc list-inside">
                        <li>Las entradas son individuales</li>
                        <li>Consumición mínima: $15.000 por persona</li>
                        <li>Las entradas no tienen cambio ni devolución</li>
                    </ul>
                </div>
            </div>

            {evento.artista && <InstagramYouTubeSection artista={evento.artista} />}
            <ComoLlegar />
        </div>
    );
};
